import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { FieldDriveDef } from './ShipDef';

export type FieldDriveMode =
  | 'off'
  | 'gravityCompensation'
  | 'hover'
  | 'ascent'
  | 'descentControl'
  | 'emergencyLift';

export interface FieldDriveInput {
  /** Local gravitational acceleration vector at the vessel, m/s^2. */
  gravity: Vec3;
  /** Vessel mass, kg. */
  mass: number;
  /** Vertical speed relative to the surface, m/s. Positive is up. */
  verticalSpeed: number;
  /** Requested upward acceleration for ascent mode, m/s^2. */
  targetAscentAcceleration: number;
  /** Electrical power the drive is permitted to draw this step, watts. */
  powerAvailable: number;
  /** Ambient temperature for the cooling model, K. */
  ambientTemperature: number;
  /** Convective cooling multiplier; >1 inside an atmosphere. */
  coolingMultiplier: number;
}

export interface FieldDriveOutput {
  /** Acceleration the drive contributes, m/s^2, in the same frame as gravity. */
  acceleration: Vec3;
  /** Electrical power actually consumed, watts. */
  powerUsed: number;
  /** Waste heat generated, watts. */
  heatGenerated: number;
  /** Efficiency actually achieved, 0..maxEfficiency. */
  efficiency: number;
  /** Fraction of nominal output available after thermal derating, 0..1. */
  thermalDerate: number;
  /** Why the drive is not delivering what was asked, if anything. */
  limitReason: string | null;
}

/**
 * Field-Assisted Ascent Drive.
 *
 * The one piece of invented physics in the project, and it is deliberately
 * constrained so that it changes the *economics* of leaving a planet without
 * removing any of the mechanics.
 *
 * What it does: generates a real acceleration vector applied to the vessel,
 * normally opposing the dominant gravity direction.
 *
 * What it does not do:
 *   - It never writes position or velocity. It contributes an acceleration and
 *     the integrator does the rest, so momentum, inertia and collisions are
 *     completely unaffected.
 *   - It does not switch gravity off. Gravity is still summed from every body
 *     every step; the drive simply pushes the other way.
 *   - It cannot replace an interplanetary engine. Efficiency scales with the
 *     local gravitational field strength, so in deep space it falls to a few
 *     percent of nominal and is not worth the reactor output.
 *   - It is not free. It draws very large electrical power and dumps a third of
 *     that back as heat into a coil assembly with finite thermal mass. Sustained
 *     use forces a cooling pause.
 *
 * The efficiency curve is the balancing mechanism:
 *
 *     efficiency = clamp(|g_local| / g_ref, minEfficiency, maxEfficiency)
 */
export class FieldAscentDrive {
  readonly def: FieldDriveDef;
  mode: FieldDriveMode = 'off';
  /** Coil temperature, K. */
  temperature = 290;
  /** 0 = pristine, 1 = destroyed. */
  damage = 0;
  /** Manual output limit, 0..1. */
  outputLimit = 1;

  /** Last computed values, kept for the HUD and diagnostics. */
  lastPowerUsed = 0;
  lastHeat = 0;
  lastEfficiency = 0;
  lastAcceleration = 0;

  constructor(def: FieldDriveDef) {
    this.def = def;
    this.temperature = 290;
  }

  get isActive(): boolean {
    return this.mode !== 'off' && this.damage < 1;
  }

  /** Efficiency at a given local gravity magnitude. */
  efficiencyAt(gravityMagnitude: number): number {
    const d = this.def;
    return clamp(gravityMagnitude / d.referenceGravity, d.minEfficiency, d.maxEfficiency);
  }

  /**
   * Thermal derating. Above `thermalLimit` output falls linearly, reaching zero
   * at `thermalCutoff`. This is what forces cooling intervals rather than an
   * arbitrary cooldown timer.
   */
  thermalDerate(): number {
    const d = this.def;
    if (this.temperature <= d.thermalLimit) return 1;
    if (this.temperature >= d.thermalCutoff) return 0;
    return 1 - (this.temperature - d.thermalLimit) / (d.thermalCutoff - d.thermalLimit);
  }

  /** Peak acceleration available right now, m/s^2. */
  availableAcceleration(gravityMagnitude: number): number {
    if (this.damage >= 1) return 0;
    const health = 1 - this.damage;
    return (
      this.def.maxAcceleration *
      this.efficiencyAt(gravityMagnitude) *
      this.thermalDerate() *
      clamp01(this.outputLimit) *
      health
    );
  }

  /**
   * Computes this step's contribution. Does not mutate the vessel; the caller
   * applies the returned acceleration as a force through the centre of mass.
   */
  update(input: FieldDriveInput, dt: number): FieldDriveOutput {
    const d = this.def;
    const gMag = input.gravity.length();
    const efficiency = this.efficiencyAt(gMag);
    const derate = this.thermalDerate();

    const output: FieldDriveOutput = {
      acceleration: new Vec3(),
      powerUsed: 0,
      heatGenerated: 0,
      efficiency,
      thermalDerate: derate,
      limitReason: null,
    };

    this.coolStep(input, dt);

    if (this.mode === 'off' || this.damage >= 1) {
      this.lastPowerUsed = 0;
      this.lastHeat = 0;
      this.lastEfficiency = efficiency;
      this.lastAcceleration = 0;
      if (this.damage >= 1) output.limitReason = 'Field drive destroyed';
      return output;
    }

    if (derate <= 0) {
      output.limitReason = 'Coil over temperature';
      this.lastAcceleration = 0;
      this.lastPowerUsed = 0;
      this.lastHeat = 0;
      return output;
    }

    if (gMag < 1e-6) {
      output.limitReason = 'No gravitational field to couple against';
      this.lastAcceleration = 0;
      return output;
    }

    const upDir = input.gravity.clone().mulScalar(-1 / gMag);
    const maxAccel = this.availableAcceleration(gMag);

    // Decide the requested acceleration magnitude from the mode.
    let requested = 0;
    switch (this.mode) {
      case 'gravityCompensation':
        requested = gMag;
        break;
      case 'hover':
        // Cancel gravity, plus a proportional term against vertical drift.
        requested = gMag - input.verticalSpeed * 1.6;
        break;
      case 'ascent':
        requested = gMag + Math.max(0, input.targetAscentAcceleration);
        break;
      case 'descentControl':
        // Only fights the descent; never pushes the vessel upward.
        requested = input.verticalSpeed < 0 ? Math.min(gMag, gMag * 0.85 + input.verticalSpeed * -0.5) : 0;
        break;
      case 'emergencyLift':
        requested = maxAccel;
        break;
      default:
        requested = 0;
    }

    requested = clamp(requested, 0, maxAccel);
    if (requested <= 0) {
      this.lastAcceleration = 0;
      this.lastPowerUsed = 0;
      this.lastHeat = 0;
      return output;
    }

    // Power gate. The drive scales its output down to whatever the electrical
    // system can actually deliver rather than drawing a bus it does not have.
    let force = requested * input.mass;
    let power = force * d.powerPerNewton;
    if (power > input.powerAvailable && input.powerAvailable >= 0) {
      const scale = input.powerAvailable / power;
      force *= scale;
      requested *= scale;
      power = input.powerAvailable;
      output.limitReason = 'Insufficient electrical power';
    }

    if (this.mode === 'emergencyLift') {
      // Emergency lift trades heat for thrust: the same force costs 2.2x the
      // power and dumps proportionally more into the coils.
      power *= 2.2;
    }

    const heat = power * d.heatFraction;
    this.temperature += (heat * dt) / Math.max(1, d.heatCapacity);

    if (requested >= maxAccel - 1e-9 && output.limitReason === null) {
      output.limitReason = derate < 1 ? 'Thermal derating' : 'At maximum field output';
    }

    output.acceleration.copy(upDir).mulScalar(requested);
    output.powerUsed = power;
    output.heatGenerated = heat;

    this.lastPowerUsed = power;
    this.lastHeat = heat;
    this.lastEfficiency = efficiency;
    this.lastAcceleration = requested;

    // Sustained overload degrades the coils permanently.
    if (this.temperature > d.thermalCutoff) {
      this.damage = clamp01(this.damage + dt * 0.02);
    }

    return output;
  }

  private coolStep(input: FieldDriveInput, dt: number): void {
    const d = this.def;
    const delta = this.temperature - input.ambientTemperature;
    if (delta <= 0) return;
    const cooling = d.coolingCoefficient * Math.max(0.2, input.coolingMultiplier) * delta;
    this.temperature -= (cooling * dt) / Math.max(1, d.heatCapacity);
    if (this.temperature < input.ambientTemperature) this.temperature = input.ambientTemperature;
  }

  /** Seconds of continuous operation before thermal cutoff at the current draw. */
  estimateTimeToCutoff(): number {
    if (this.lastHeat <= 0) return Infinity;
    const headroom = (this.def.thermalCutoff - this.temperature) * this.def.heatCapacity;
    return Math.max(0, headroom / this.lastHeat);
  }
}
